/**
 * SQL-safety validation for dataset names, workspace IDs, and column names.
 *
 * @deckspace/validation depends only on @deckspace/errors. All Deckspace
 * packages that interpolate user-supplied identifiers into SQL should pass
 * the identifier through these validators first.
 *
 * Rules in one place:
 * - Dataset/column names: start with a letter, alphanumeric + underscore,
 *   not a SQL reserved word, ≤ 64 chars.
 * - Workspace IDs: a UUID, or kebab-case alphanumeric (≤ 64 chars after
 *   hyphen→underscore normalization).
 */
import { DeckspaceError } from '@deckspace/errors';
/** Pattern for valid dataset/column names: starts with letter, alphanumeric + underscore. */
const VALID_DATASET_NAME = /^[a-z][a-z0-9_]*$/i;
/** UUID pattern: 8-4-4-4-12 hex digits. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** SQL reserved words that cannot be used as dataset or column names. */
const RESERVED_WORDS = [
    'select',
    'from',
    'where',
    'insert',
    'update',
    'delete',
    'table',
    'create',
    'drop',
    'alter',
    'index',
    'join',
    'inner',
    'outer',
    'left',
    'right',
    'on',
    'and',
    'or',
    'not',
    'null',
    'true',
    'false',
    'group',
    'order',
    'by',
    'having',
    'limit',
    'offset',
    'union',
    'all',
    'distinct',
    'as',
    'in',
    'is',
    'like',
    'between',
    'case',
    'when',
    'then',
    'else',
    'end',
    'primary',
    'foreign',
    'key',
    'references',
    'constraint',
    'default',
    'unique',
    'check',
];
// Column names reserved for Deckspace system metadata. Distinct from
// RESERVED_WORDS (SQL keywords) — these are reserved by the platform,
// not by SQL. Per ADR-0001, `id` belongs to the system namespace.
const RESERVED_SYSTEM_COLUMN_NAMES = new Set(['id']);
/** Maximum dataset/column/workspace-id length. */
const MAX_DATASET_NAME_LENGTH = 64;
/**
 * Validate a dataset name for safety and conventions.
 *
 * Rules:
 * - Must start with a letter
 * - Can only contain letters, numbers, underscores
 * - Cannot start with `_` (reserved for system datasets)
 * - Cannot be a SQL reserved word
 * - Maximum 64 characters
 *
 * Throws `DeckspaceError` if validation fails.
 */
export function validateDatasetName(name) {
    if (!name || name.length === 0) {
        throw new DeckspaceError('INVALID_DATASET_NAME', 'Dataset name cannot be empty');
    }
    if (!VALID_DATASET_NAME.test(name)) {
        throw new DeckspaceError('INVALID_DATASET_NAME', `Dataset name must start with letter and contain only letters, numbers, underscores: "${name}"`);
    }
    if (name.startsWith('_')) {
        throw new DeckspaceError('RESERVED_PREFIX', `Dataset names starting with '_' are reserved for system use`);
    }
    if (RESERVED_WORDS.includes(name.toLowerCase())) {
        throw new DeckspaceError('INVALID_DATASET_NAME', `Dataset name cannot be a SQL reserved word: "${name}"`);
    }
    if (name.length > MAX_DATASET_NAME_LENGTH) {
        throw new DeckspaceError('INVALID_DATASET_NAME', `Dataset name too long (max ${MAX_DATASET_NAME_LENGTH} chars): "${name}"`);
    }
}
/**
 * Validate a workspace ID.
 *
 * Accepts:
 * - UUIDs (8-4-4-4-12 hex format)
 * - Legacy kebab-case strings (for name lookups)
 *
 * This validation is permissive. Actual resolution happens in `resolveWorkspaceId()`.
 */
export function validateWorkspaceId(workspaceId) {
    if (!workspaceId || workspaceId.length === 0) {
        throw new DeckspaceError('WORKSPACE_NOT_FOUND', 'Workspace ID cannot be empty');
    }
    // Accept UUIDs.
    if (UUID_PATTERN.test(workspaceId)) {
        return;
    }
    // Allow kebab-case for workspace IDs (convert to underscore for SQL).
    const normalizedId = workspaceId.replace(/-/g, '_');
    if (!VALID_DATASET_NAME.test(normalizedId)) {
        throw new DeckspaceError('INVALID_DATASET_NAME', `Workspace ID must be UUID or alphanumeric with hyphens/underscores: "${workspaceId}"`);
    }
    if (workspaceId.length > MAX_DATASET_NAME_LENGTH) {
        throw new DeckspaceError('INVALID_DATASET_NAME', `Workspace ID too long (max ${MAX_DATASET_NAME_LENGTH} chars): "${workspaceId}"`);
    }
}
/**
 * Validate a column name for SQL safety.
 *
 * Column names follow the same rules as dataset names.
 */
export function validateColumnName(name) {
    if (!name || name.length === 0) {
        throw new DeckspaceError('INVALID_COLUMN_NAME', 'Column name cannot be empty');
    }
    if (RESERVED_SYSTEM_COLUMN_NAMES.has(name.toLowerCase())) {
        throw new DeckspaceError('INVALID_COLUMN_NAME', `Column name '${name}' is reserved for Deckspace system metadata. Pick a different name.`);
    }
    if (!VALID_DATASET_NAME.test(name)) {
        throw new DeckspaceError('INVALID_COLUMN_NAME', `Column name must start with letter and contain only letters, numbers, underscores: "${name}"`);
    }
    if (RESERVED_WORDS.includes(name.toLowerCase())) {
        throw new DeckspaceError('INVALID_COLUMN_NAME', `Column name cannot be a SQL reserved word: "${name}"`);
    }
}
//# sourceMappingURL=index.js.map